import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef } from 'react';

import {
  BackPriority,
  chromeFocusKeys,
  doesFocusableExist,
  FocusContext,
  getCurrentFocusKey,
  getScreenFocusDefault,
  isInFocusScope,
  managedFocusKeys,
  navigateByDirection,
  ROOT_FOCUS_KEY,
  setFocus,
  useBackHandler,
  useFocusable,
} from '../focus';
import { IconChevronRight } from '../icons';
import { Badge } from './Badge';
import { cx } from './cx';
import styles from './Rail.module.css';

export const RAIL_FOCUS_KEY = 'rail';

/** True while the rail is drawn as the expanded overlay (docs/04 §7.1). */
const ExpandedContext = createContext(false);
/** Collapses the rail and returns the focus to the content's last focused element. */
const LeaveContext = createContext<() => void>(() => {});

export interface RailItemProps {
  itemKey: string;
  label: string;
  icon: ReactNode;
  /** Current screen (cyan icon + label, cyan left bar). */
  active?: boolean;
  /** Counter badge (e.g. queued songs); hidden when 0/undefined. */
  badge?: number;
  onSelect?: (key: string) => void;
  onFocus?: (key: string) => void;
}

export function RailItem({
  itemKey,
  label,
  icon,
  active,
  badge,
  onSelect,
  onFocus,
}: RailItemProps) {
  const expanded = useContext(ExpandedContext);
  const leave = useContext(LeaveContext);
  const {
    ref,
    focused,
    focusKey: key,
  } = useFocusable<HTMLDivElement>({
    focusKey: `${RAIL_FOCUS_KEY}-${itemKey}`,
    onEnterPress: () => onSelect?.(itemKey),
    onFocus: () => onFocus?.(itemKey),
    // Right never walks into the content by geometry (the overlay covers it): it collapses
    // the rail and hands the focus back to whatever was focused before (docs/04 §7.1).
    onArrowPress: (direction) => {
      if (direction !== 'right') return true;
      leave();
      return false;
    },
  });
  return (
    <div
      ref={ref}
      role="tab"
      aria-selected={active || undefined}
      data-focus-key={key}
      data-focused={focused || undefined}
      data-testid={`rail-${itemKey}`}
      className={cx(styles.item, active && styles.active, focused && styles.focused)}
    >
      <span className={styles.bar} aria-hidden />
      <span className={styles.icon}>{icon}</span>
      <span className={styles.label}>{label}</span>
      {badge ? (
        <>
          <span className={styles.badgeSpacer} aria-hidden />
          <Badge tone={focused && expanded ? 'count-inverted' : 'count'} className={styles.badge}>
            {badge}
          </Badge>
        </>
      ) : null}
    </div>
  );
}

export interface RailQueueEntry {
  id: string;
  title: string;
  subtitle?: string;
}

/**
 * The queue, spelled out inside the expanded rail (UX-192). The collapsed rail carries a count
 * badge and nothing else, so a room adding songs from its phones could not tell *what* was
 * waiting or whose turn it was — only that a number had gone up. Rendered only while the rail
 * is expanded, and never focusable: it is a read-out, "Fila" is the way in.
 */
export function RailQueue({
  title,
  entries,
  empty,
  more,
}: {
  title: string;
  entries: RailQueueEntry[];
  empty: string;
  /** Copy for the items beyond the ones listed ("+3 más en fila"). */
  more?: string;
}) {
  const expanded = useContext(ExpandedContext);
  if (!expanded) return null;
  return (
    <div className={styles.queue} data-testid="rail-queue-list">
      <div className={styles.queueTitle}>{title}</div>
      {entries.length === 0 ? (
        <p className={styles.queueEmpty}>{empty}</p>
      ) : (
        <ol className={styles.queueList}>
          {entries.map((entry, index) => (
            <li key={entry.id} className={styles.queueItem}>
              <span className={styles.queueIndex} aria-hidden>
                {index + 1}
              </span>
              <span className={styles.queueText}>
                <span className={styles.queueSong}>{entry.title}</span>
                {entry.subtitle ? <span className={styles.queueSub}>{entry.subtitle}</span> : null}
              </span>
            </li>
          ))}
        </ol>
      )}
      {more ? <p className={styles.queueMore}>{more}</p> : null}
    </div>
  );
}

export interface RailProps {
  children: ReactNode;
  /** Brand block rendered above the items. */
  brand?: ReactNode;
  /** Copy under the items while expanded. */
  hint?: string;
  /**
   * Read-out pinned to the bottom of the expanded rail (the app puts the phone QR there,
   * owner 2026-10-05). Rendered only while expanded and never focusable.
   */
  footer?: ReactNode;
  /**
   * Expand as an overlay when the rail gains focus (docs/04 §7.1). Default true; tests and
   * the component gallery use `false` to pin one of the two states.
   */
  collapsible?: boolean;
  focusKey?: string;
  className?: string;
}

/**
 * Left navigation rail (docs/04 §7.1). Collapsed it is an icon-only 96 px column inside the
 * safe area; when it gains focus it expands into a 456 px overlay **above** the content — the
 * content never reflows — with a scrim behind it. Left from the content enters it, Right or
 * Enter collapse it, Back collapses it. It is a focus boundary for up/down/left, so only the
 * explicit exits leave, and it remembers its last focused item.
 */
export function Rail({
  children,
  brand,
  hint,
  footer,
  collapsible = true,
  focusKey = RAIL_FOCUS_KEY,
  className,
}: RailProps) {
  const {
    ref,
    focusKey: key,
    hasFocusedChild,
  } = useFocusable<HTMLElement>({
    focusKey,
    trackChildren: true,
    saveLastFocusedChild: true,
    isFocusBoundary: true,
    focusBoundaryDirections: ['up', 'down', 'left'],
  });
  const expanded = collapsible ? hasFocusedChild : true;

  // Where the focus came from. Norigin has no focus-change event, so the key is snapshotted
  // on every key-down before the navigation runs: the last one outside the rail is the
  // content element the D-pad left behind.
  const outside = useRef<string | null>(null);
  useEffect(() => {
    const onKeyDown = () => {
      const current = getCurrentFocusKey();
      if (!current || current === ROOT_FOCUS_KEY || current.startsWith(focusKey)) return;
      // UX-340: a key inside an open overlay (the queue drawer, a dialog) is *not* the content
      // the D-pad left behind — it is unmounted with the overlay. Remembering one left `leave()`
      // pointing at a dead focusable, and since `leave()` is what both Right and Back run, Home
      // stopped answering either of them for as long as the focus stayed in the rail.
      if (isInFocusScope(current)) return;
      outside.current = current;
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [focusKey]);

  /**
   * Hands the focus back to the content (docs/04 §7.1).
   *
   * UX-340: this must always land somewhere. The remembered element can be gone (an overlay
   * closed, a list re-rendered, the screen swapped underneath the rail), and the spatial
   * fallback cannot be trusted either — the expanded rail is a 456 px overlay drawn *over* a
   * content column that starts at 144 px, so "the first card to the right" is not to the right
   * of anything in the rail and norigin refuses the move. So: the element the focus came from,
   * else the screen's declared default, else the first content focusable on screen, and only
   * then the spatial move.
   */
  const leave = useCallback(() => {
    const target = outside.current;
    if (target && doesFocusableExist(target) && !isInFocusScope(target)) {
      void setFocus(target);
      return;
    }
    const { defaultFocusKey } = getScreenFocusDefault();
    if (defaultFocusKey && doesFocusableExist(defaultFocusKey)) {
      void setFocus(defaultFocusKey);
      return;
    }
    const chrome = chromeFocusKeys(document);
    const first = managedFocusKeys(document).find(
      (candidate) => !chrome.includes(candidate) && doesFocusableExist(candidate),
    );
    if (first) {
      void setFocus(first);
      return;
    }
    navigateByDirection('right', {});
  }, []);

  // Back while expanded collapses instead of popping the screen (docs/04 §7.1).
  useBackHandler(
    () => {
      if (!expanded || !collapsible) return false;
      leave();
      return true;
    },
    { priority: BackPriority.overlay },
  );

  return (
    <FocusContext.Provider value={key}>
      <ExpandedContext.Provider value={expanded}>
        <LeaveContext.Provider value={leave}>
          {expanded && collapsible ? (
            <div className={styles.scrim} role="presentation" data-testid="rail-scrim" />
          ) : null}
          <nav
            ref={ref}
            className={cx(styles.rail, expanded ? styles.expanded : styles.collapsed, className)}
            data-focus-chrome="rail"
            /* UX-011: Down at the last rail item nudges the rail (it never wraps). */
            data-edge-cue-target=""
            data-collapsed={expanded ? undefined : 'true'}
            data-expanded={expanded ? 'true' : undefined}
            aria-label="rail"
          >
            {brand ? <div className={styles.brand}>{brand}</div> : null}
            {children}
            {expanded ? (
              <>
                {hint ? <p className={styles.hint}>{hint}</p> : null}
                {footer ? (
                  <div className={styles.footer} data-testid="rail-footer">
                    {footer}
                  </div>
                ) : null}
              </>
            ) : (
              <span className={styles.expandHint} role="presentation" aria-hidden>
                <IconChevronRight size={20} />
              </span>
            )}
          </nav>
        </LeaveContext.Provider>
      </ExpandedContext.Provider>
    </FocusContext.Provider>
  );
}

export function Brand({ name }: { name: string }) {
  return (
    <span className={styles.brandInner} role="img" aria-label={name}>
      <span className={styles.brandMark} />
      <span className={styles.brandName} />
    </span>
  );
}
